import { useEffect, useState } from 'react';

import type { TFileDropzoneFile } from '@vlabs/monokit/features/file-with-detections-viewer/FileWithDetectionsViewer';
import type { TDetection } from '@vlabs/shared/components/photo-upload-form';
import { detectOnImageRequest, DETECTION_TYPES } from '@vlabs/shared/requests/detect-on-image-request';

import { useSearchApi } from '../../api/SearchApiProvider';

export interface TDetections {
  all: TDetection[];
  bodies: TDetection[];
  faces: TDetection[];
  byId: Record<string, TDetection>;
}

interface UseDetectionsResult {
  detections: TDetections | undefined;
  isLoading: boolean;
}

function useDetections(file: TFileDropzoneFile | undefined): UseDetectionsResult {
  const api = useSearchApi();
  const [detections, setDetections] = useState<TDetections>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!file) {
      setDetections(undefined);
      setIsLoading(false);
      return undefined;
    }
    const currentFile = file;
    let cancelled = false;

    async function detectImage() {
      try {
        setIsLoading(true);
        const imageDetections = await detectOnImageRequest(
          currentFile,
          [DETECTION_TYPES.FACES, DETECTION_TYPES.BODIES],
          api.estimateFace,
          api.estimateBody,
        );
        if (cancelled) return;

        if (!Array.isArray(imageDetections)) {
          setDetections(imageDetections);
          return;
        }

        setDetections({
          all: [],
          bodies: [],
          faces: [],
          byId: {},
        });
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    detectImage();

    return () => {
      cancelled = true;
    };
  }, [api.estimateBody, api.estimateFace, file]);

  return { detections, isLoading };
}

export { useDetections };
